import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Countdown } from '../components/Countdown';
import { LarkwellMark } from '../components/LarkwellMark';
import { useSeo } from '../lib/useSeo';
import {
  CalendarClock, Sparkles, Inbox, Timer, Brain, ListChecks, Waves, Type,
  ArrowRight, Clock3, Layers, Zap, Heart, Twitter, Linkedin, Github, CheckCircle2, Coffee, Bell, Crown, Mail, Star, Quote, Tag,
  ShieldCheck, Lock, Download, Trash2,
} from 'lucide-react';

const fade = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } } };

function WaitlistBand() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const val = email.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { toast.error('Please enter a valid email.'); return; }
    setBusy(true);
    try {
      const res = await api.post('/public/waitlist', { email: val, source: 'landing' });
      setDone(true);
      toast.success(res.data?.already ? "You're already on the list — thank you!" : "You're on the list! We'll be in touch. 💛");
    } catch (err) { toast.error("Couldn't add you just now — please try again."); } finally { setBusy(false); }
  };

  return (
    <section className="border-t border-[hsl(var(--border))]" data-testid="waitlist-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="inline-flex h-11 w-11 rounded-2xl items-center justify-center mb-4" style={{ background: 'hsl(var(--primary) / 0.12)' }}>
          <Mail className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-700">Not ready yet? Join the list.</h2>
        <p className="text-[hsl(var(--muted-foreground))] mt-2 max-w-xl mx-auto">Get launch news and be first in line when new founding-style deals open up. No spam, unsubscribe anytime.</p>
        {done ? (
          <div className="mt-6 inline-flex items-center gap-2 rounded-xl px-5 py-3 font-600" style={{ background: 'hsl(var(--success) / 0.12)', color: 'hsl(var(--success))' }} data-testid="waitlist-success">
            <CheckCircle2 className="h-5 w-5" /> You're on the list — talk soon!
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row items-center gap-2 max-w-md mx-auto">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12 rounded-xl flex-1 w-full" data-testid="waitlist-email-input" aria-label="Email address" />
            <Button type="submit" disabled={busy} className="h-12 rounded-xl px-6 w-full sm:w-auto shrink-0" style={{ background: 'hsl(var(--primary))' }} data-testid="waitlist-submit-button">{busy ? 'Joining…' : 'Join the list'}</Button>
          </form>
        )}
      </div>
    </section>
  );
}

// Compact inline waitlist used in the hero once the founding offer ends
function InlineWaitlist({ source = 'hero' }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const val = email.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { toast.error('Please enter a valid email.'); return; }
    setBusy(true);
    try {
      const res = await api.post('/public/waitlist', { email: val, source });
      setDone(true);
      toast.success(res.data?.already ? "You're already on the list — thank you!" : "You're on the list! We'll be in touch. 💛");
    } catch (err) { toast.error("Couldn't add you just now — please try again."); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mt-7 inline-flex items-center gap-2 rounded-xl px-5 py-3 font-600" style={{ background: 'hsl(var(--success) / 0.12)', color: 'hsl(var(--success))' }} data-testid="hero-waitlist-success">
        <CheckCircle2 className="h-5 w-5" /> You're on the list — talk soon!
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-md" data-testid="hero-waitlist-form">
      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12 rounded-xl flex-1 w-full" data-testid="hero-waitlist-email-input" aria-label="Email address" />
      <Button type="submit" disabled={busy} className="h-12 rounded-xl px-6 w-full sm:w-auto shrink-0 gap-1.5" style={{ background: 'hsl(var(--primary))' }} data-testid="hero-waitlist-submit-button">
        {busy ? 'Joining…' : <>Join the waitlist <ArrowRight className="h-4 w-4" /></>}
      </Button>
    </form>
  );
}

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-xl grid place-items-center text-white" style={{ background: 'hsl(var(--primary))' }}><LarkwellMark className="h-5 w-5" stroke="#ffffff" /></div>
      <span className="font-display text-xl font-700 tracking-tight">Larkwell</span>
    </div>
  );
}

function PlanMock() {
  const blocks = [
    { t: 'Deep work: board deck', time: '9:00–9:45', bar: 'hsl(var(--brand-mint))', tint: 'hsla(165,55%,40%,0.12)' },
    { t: 'Short reset', time: '9:45–9:55', bar: 'hsl(var(--muted-foreground))', tint: 'hsla(28,30%,90%,0.6)' },
    { t: 'Reply to Priya', time: '10:00–10:20', bar: 'hsl(var(--primary))', tint: 'hsla(18,92%,52%,0.12)' },
    { t: 'Team standup', time: '10:30–11:00', bar: 'hsl(var(--warning))', tint: 'hsla(38,92%,52%,0.14)' },
  ];
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card shadow-[0_30px_80px_-40px_hsla(222,35%,12%,0.35)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center gap-2">
        <CalendarClock className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
        <p className="text-sm font-600">Today — a gentle plan</p>
        <Badge className="ml-auto text-[10px]" style={{ background: 'hsl(var(--success) / 0.12)', color: 'hsl(var(--success))' }}>Room to breathe</Badge>
      </div>
      <div className="p-4 grid grid-cols-[1fr_150px] gap-4">
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <div key={i} className="relative rounded-xl border border-[hsl(var(--border))] pl-3 pr-3 py-2" style={{ background: b.tint }}>
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl" style={{ background: b.bar }} />
              <p className="text-sm font-600">{b.t}</p>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{b.time}</p>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <div className="rounded-xl border border-[hsl(var(--border))] p-3 text-center">
            <div className="relative h-16 w-16 mx-auto">
              <svg viewBox="0 0 120 120" className="h-16 w-16 -rotate-90"><circle cx="60" cy="60" r="52" fill="none" stroke="hsl(var(--secondary))" strokeWidth="12" /><circle cx="60" cy="60" r="52" fill="none" stroke="hsl(var(--primary))" strokeWidth="12" strokeLinecap="round" strokeDasharray="327" strokeDashoffset="120" /></svg>
              <div className="absolute inset-0 grid place-items-center"><span className="font-mono text-xs font-600">18:24</span></div>
            </div>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Focus sprint</p>
          </div>
          <div className="rounded-xl border border-[hsl(var(--border))] p-3">
            <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Capacity</p>
            <div className="h-2 rounded-full bg-[hsl(var(--secondary))] mt-1.5 overflow-hidden"><div className="h-full rounded-full" style={{ width: '62%', background: 'hsl(var(--primary))' }} /></div>
            <p className="text-[10px] mt-1.5">4.5h / 7h</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const PROBLEMS = [
  { icon: Clock3, title: 'Time blindness', text: '“Five minutes” becomes an hour. Larkwell makes time visible with blocks you can actually see.' },
  { icon: Layers, title: 'Overwhelm', text: 'A wall of to-dos freezes you. Larkwell shrinks the mountain into one tiny next step.' },
  { icon: Zap, title: 'Task initiation', text: 'Starting is the hardest part. A 2-minute first step and a focus timer get you moving.' },
];

const FEATURES = [
  { icon: CalendarClock, title: 'Today planner', text: 'Visual time-blocking with a capacity meter so you never overbook your energy.', span: 'lg:col-span-2' },
  { icon: Sparkles, title: 'AI Plan My Day', text: 'One tap turns your tasks into a realistic, break-friendly schedule.' },
  { icon: Inbox, title: 'Brain-dump inbox', text: 'Catch every racing thought in seconds. Sort it later — nothing gets lost.' },
  { icon: ListChecks, title: 'AI task breakdown', text: 'Turn one scary task into 3–6 doable steps, starting with a 2-minute win.' },
  { icon: Timer, title: 'Focus timer', text: 'Body-doubling sprints with gentle 10/5/0-minute transition alerts.', span: 'lg:col-span-2' },
  { icon: Bell, title: 'Gentle nudges', text: 'Nag mode nudges you kindly until the important thing is done.' },
  { icon: Brain, title: 'Living memory', text: 'Your AI remembers your context, so you never re-explain yourself.' },
];

const QUOTES = [
  { q: 'For the first time a planner actually matches how my brain jumps around. The tiny-steps thing is magic.', n: 'Maya R.', r: 'Product designer' },
  { q: 'The capacity meter stopped me overbooking myself into burnout. Genuinely life-changing at work.', n: 'Daniel K.', r: 'Engineering lead' },
  { q: 'Brain-dump + AI breakdown means I finally start things instead of staring at them.', n: 'Priya S.', r: 'Founder' },
];

export default function Landing() {
  const navigate = useNavigate();
  useSeo({
    title: 'Larkwell — Work with your ADHD brain',
    description: 'Larkwell is an AI day planner for ADHD brains. Brain-dump your thoughts, plan a realistic day in one tap, focus with visible timers, and sync your calendar.',
    canonical: 'https://larkwellapp.com/',
  });
  const go = () => navigate('/login');
  const [founding, setFounding] = useState(null);
  const [promo, setPromo] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => { api.get('/public/founding').then((r) => setFounding(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/public/promo').then((r) => setPromo(r.data?.promo)).catch(() => {}); }, []);
  useEffect(() => { api.get('/public/testimonials').then((r) => setTestimonials(r.data?.testimonials || [])).catch(() => {}); }, []);
  const seatsLeft = founding?.seats_remaining;
  const claimed = founding?.seats_claimed || 0;
  const total = founding?.seats_total || 100;
  const pct = Math.min(Math.round((claimed / total) * 100), 100);
  const offerClosed = founding?.offer_closed;
  const deadline = founding?.deadline;
  return (
    <div className="min-h-dvh bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      {/* Founding member banner */}
      {founding && !offerClosed && (
        <button onClick={go} className="w-full text-white text-center text-sm py-2 px-4 flex items-center justify-center gap-x-2 gap-y-0.5 flex-wrap hover:opacity-95 transition-opacity" style={{ background: 'hsl(var(--primary))' }} data-testid="founding-banner">
          <Zap className="h-4 w-4" />
          <span className="font-600">Only {seatsLeft} of {total} Founding seats left</span>
          {deadline && <span className="opacity-90 inline-flex items-center gap-1">— closes in <Countdown deadline={deadline} inline className="text-white" testId="banner-countdown" /></span>}
          <span className="hidden sm:inline opacity-90">· lifetime Pro for $99 →</span>
          {promo && <span className="inline-flex items-center gap-1 font-700 rounded-full px-2 py-0.5" style={{ background: 'rgba(255,255,255,0.22)' }} data-testid="landing-promo-pill"><Tag className="h-3.5 w-3.5" /> {promo.code} = {promo.label}</span>}
        </button>
      )}
      {founding && offerClosed && (
        <div className="w-full text-center text-sm py-2 px-4 bg-[hsl(var(--foreground))] text-white" data-testid="founding-banner">
          {founding.expired && seatsLeft > 0
            ? 'The Founding Member offer has closed — thank you! Pro subscription launching soon.'
            : 'All 100 Founding seats are claimed — thank you! Pro subscription launching soon.'}
        </div>
      )}
      <header className="sticky top-0 z-50 bg-card/70 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-7 text-sm text-[hsl(var(--foreground))]/70">
            <a href="#problem" className="hover:text-[hsl(var(--foreground))] transition-colors">Why Larkwell</a>
            <a href="#features" className="hover:text-[hsl(var(--foreground))] transition-colors">Features</a>
            <a href="#voices" className="hover:text-[hsl(var(--foreground))] transition-colors">Voices</a>
            <a href="#mission" className="hover:text-[hsl(var(--foreground))] transition-colors">Mission</a>
            <a href="#access" className="hover:text-[hsl(var(--foreground))] transition-colors">Accessibility</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="rounded-xl hidden sm:inline-flex" onClick={go} data-testid="landing-signin-button">Sign in</Button>
            <Button className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={go} data-testid="landing-primary-cta-button">Start free</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 hero-wash" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <motion.div variants={fade} initial="hidden" animate="show" className="lg:col-span-6">
              <Badge variant="secondary" className="rounded-full gap-1.5 mb-5 border border-[hsl(var(--border))]"><Heart className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} /> {offerClosed ? 'Built for professionals with ADHD' : (seatsLeft > 0 ? `Founding offer · ${seatsLeft} of ${total} seats left` : 'Built for professionals with ADHD')}</Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-700 tracking-[-0.02em] leading-[1.05]">
                Your brain wasn't built for endless lists.<span style={{ color: 'hsl(var(--primary))' }}> Larkwell works with it.</span>
              </h1>
              <p className="mt-5 text-base md:text-lg text-[hsl(var(--muted-foreground))] max-w-xl">
                The AI teammate for busy, ADHD brains: dump the chaos, tap once, and watch it become a calm, doable day — tiny steps, small wins, and no shame.
              </p>
              {offerClosed ? (
                <>
                  <p className="mt-6 text-sm font-600 flex items-center gap-2" style={{ color: 'hsl(var(--primary))' }} data-testid="hero-closed-note">
                    <Mail className="h-4 w-4" /> Founding seats are all claimed — join the waitlist for launch news.
                  </p>
                  <InlineWaitlist source="hero" />
                  <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="mt-4 text-sm font-600 inline-flex items-center gap-1.5 hover:underline" style={{ color: 'hsl(var(--foreground))' }} data-testid="hero-see-how-link">
                    See how it works <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Button size="lg" className="rounded-xl h-12 px-6" style={{ background: 'hsl(var(--primary))' }} onClick={go} data-testid="hero-primary-cta-button">
                    Plan my first day <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                  <Button size="lg" variant="secondary" className="rounded-xl h-12 px-6 border border-[hsl(var(--border))]" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
                    See how it works
                  </Button>
                </div>
              )}
              <div className="mt-6 flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'hsl(var(--success))' }} /> {offerClosed ? 'No spam, unsubscribe anytime' : 'Free to start'}</span>
                <span className="flex items-center gap-1.5"><Waves className="h-3.5 w-3.5" /> Low-stimulation mode</span>
              </div>
            </motion.div>
            <motion.div variants={fade} initial="hidden" animate="show" transition={{ delay: 0.12 }} className="lg:col-span-6">
              <PlanMock />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section id="problem" className="border-y border-[hsl(var(--border))] bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl"><p className="text-sm font-600" style={{ color: 'hsl(var(--primary))' }}>The ADHD tax is real</p>
            <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight mt-2">It's not a willpower problem</h2>
            <p className="text-[hsl(var(--muted-foreground))] mt-3">Larkwell is designed around the three things that trip up ADHD brains at work.</p></div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mt-10">
            {PROBLEMS.map((p, i) => (
              <motion.div key={p.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-6">
                <div className="h-11 w-11 rounded-xl grid place-items-center mb-3" style={{ background: 'hsl(var(--primary) / 0.1)' }}><p.icon className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /></div>
                <h3 className="font-display font-600 text-lg">{p.title}</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-2xl"><p className="text-sm font-600" style={{ color: 'hsl(var(--accent))' }}>Everything in one calm place</p>
          <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight mt-2">Tools that meet you where you are</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-10">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-40px' }} transition={{ delay: (i % 3) * 0.05 }}
              className={`rounded-2xl border border-[hsl(var(--border))] bg-card p-5 hover:shadow-md transition-shadow ${f.span || ''}`}>
              <div className="h-10 w-10 rounded-xl grid place-items-center mb-3" style={{ background: 'hsl(var(--accent) / 0.12)' }}><f.icon className="h-5 w-5" style={{ color: 'hsl(var(--accent))' }} /></div>
              <h3 className="font-display font-600 text-lg">{f.title}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Voices */}
      <section id="voices" className="bg-card border-y border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight text-center">Loved by busy ADHD brains</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {QUOTES.map((c) => (
              <div key={c.n} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-6">
                <p className="text-[15px] leading-relaxed">“{c.q}”</p>
                <div className="mt-4 flex items-center gap-2"><div className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-600" style={{ background: 'hsl(var(--primary))' }}>{c.n[0]}</div><div><p className="text-sm font-600">{c.n}</p><p className="text-[11px] text-[hsl(var(--muted-foreground))]">{c.r}</p></div></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 max-w-3xl mx-auto">
            {[['2.4×', 'more tasks finished'], ['-38%', 'less overwhelm'], ['12k+', 'focus sprints'], ['4.9\u2605', 'avg rating']].map(([n, l]) => (
              <div key={l} className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 text-center"><p className="font-display text-2xl font-700" style={{ color: 'hsl(var(--primary))' }}>{n}</p><p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{l}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* Tester testimonials (dynamic — from featured in-app feedback) */}
      {testimonials.length > 0 && (
        <section id="testers" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20" data-testid="testers-testimonials">
          <div className="text-center">
            <Badge variant="secondary" className="rounded-full gap-1.5 mb-4 border border-[hsl(var(--border))]"><Heart className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} /> From our founding testers</Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight">In their own words</h2>
            <p className="text-[hsl(var(--muted-foreground))] mt-3 max-w-xl mx-auto">Real notes from the people trying Larkwell early with their VIP access.</p>
          </div>
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 mt-10 [column-fill:_balance]">
            {testimonials.map((t, i) => (
              <div key={`${t.name}-${i}`} className="mb-6 break-inside-avoid rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-6" data-testid={`tester-quote-${i}`}>
                <Quote className="h-5 w-5 mb-3" style={{ color: 'hsl(var(--primary))' }} />
                {t.rating ? (
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, s) => (
                      <Star key={s} className="h-4 w-4" style={{ color: 'hsl(var(--brand-honey))', fill: 'hsl(var(--brand-honey))' }} />
                    ))}
                  </div>
                ) : null}
                <p className="text-[15px] leading-relaxed">{t.quote}</p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-600" style={{ background: 'hsl(var(--primary))' }}>{(t.name || 'T')[0]}</div>
                  <div><p className="text-sm font-600">{t.name}</p><p className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.role}</p></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Accessibility */}
      <section id="access" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl font-700 tracking-tight">Accessible by design</h2>
            <p className="text-[hsl(var(--muted-foreground))] mt-3 max-w-md">Larkwell bends to your senses, not the other way around. Turn any of these on in one tap.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              {[[Waves, 'Low-stimulation mode'], [Type, 'Dyslexia-friendly font'], [Type, 'Adjustable text size']].map(([Icon, label], i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-4 py-2.5 text-sm"><Icon className="h-4 w-4" style={{ color: 'hsl(var(--accent))' }} /> {label}</div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[['Focus', Timer], ['Coffee break', Coffee], ['Small win', CheckCircle2]].map(([t, Icon]) => (
              <div key={t} className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 text-center"><Icon className="h-6 w-6 mx-auto mb-2" style={{ color: 'hsl(var(--primary))' }} /><p className="text-xs font-600">{t}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section id="mission" className="bg-card border-y border-[hsl(var(--border))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <p className="text-sm font-600" style={{ color: 'hsl(var(--primary))' }}>Our mission</p>
            <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight mt-2">Overwhelm isn't a character flaw — it's a design problem.</h2>
            <div className="mt-6 space-y-4 text-[hsl(var(--muted-foreground))] text-base sm:text-lg leading-relaxed">
              <p>Most productivity tools were built for brains that already colour inside the lines. Larkwell isn't. We're for the busy, brilliant, ten-tabs-open minds who've been told to &ldquo;just focus&rdquo; one too many times.</p>
              <p>So we built an AI teammate that works the way your brain actually works: dump the chaos, tap once, and watch it become a calm, doable day. No shame, no endless lists — just tiny steps, small wins, and the quiet confidence that comes from finally moving.</p>
            </div>
            <p className="font-display text-2xl sm:text-3xl font-700 tracking-tight mt-8">Your brain isn't broken. <span style={{ color: 'hsl(var(--primary))' }}>It just needed backup.</span></p>
            <div className="flex flex-wrap gap-2.5 mt-8">
              {['Brains-first', 'Progress over perfection', 'Calm by design', "You're in control", 'Warm & a little playful'].map((v, i) => (
                <span key={v} className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-4 py-2 text-sm font-600" data-testid={`mission-value-${i}`}>{v}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security / trust */}
      <section id="security" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20" data-testid="landing-security-section">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl">
          <p className="text-sm font-600" style={{ color: 'hsl(var(--primary))' }}>Your data, protected</p>
          <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight mt-2">Your thoughts are safe with us</h2>
          <p className="text-[hsl(var(--muted-foreground))] mt-3 text-base sm:text-lg leading-relaxed">Larkwell holds some of your most personal thoughts. We treat them with the care they deserve — encrypted, never sold, and always yours to take or erase.</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {[
            { icon: Lock, title: 'Encrypted', body: 'TLS everywhere, and sensitive tokens encrypted at rest.' },
            { icon: Download, title: 'Yours to export', body: 'Download all your data as JSON anytime, in one tap.' },
            { icon: Trash2, title: 'Yours to delete', body: 'Permanently erase your account and data whenever you want.' },
            { icon: ShieldCheck, title: 'GDPR & CCPA aligned', body: 'Built around your privacy rights. We never sell your data.' },
          ].map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={title}
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-2xl border border-[hsl(var(--border))] bg-card p-6"
              data-testid={`landing-security-card-${i}`}
            >
              <div className="h-10 w-10 rounded-xl grid place-items-center mb-4" style={{ background: 'hsl(var(--primary) / 0.12)' }}>
                <Icon className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
              </div>
              <h3 className="font-display text-lg font-600 mb-1">{title}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/security" className="inline-flex items-center gap-1.5 text-sm font-600 hover:underline" style={{ color: 'hsl(var(--primary))' }} data-testid="landing-security-learn-more">
            See how we protect your data <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-700 tracking-tight max-w-2xl mx-auto">Start with one gentle day</h2>
          <p className="text-[hsl(var(--muted-foreground))] mt-3 max-w-xl mx-auto">No overwhelm, no setup marathon. Sign in and let Larkwell sketch your next hour.</p>
          {founding && !offerClosed && (
            <div className="max-w-md mx-auto mt-8 rounded-2xl border-2 p-5 bg-card" style={{ borderColor: 'hsl(var(--primary))' }} data-testid="founding-progress-card">
              <div className="flex items-center justify-between text-sm">
                <span className="font-600 flex items-center gap-1.5"><Crown className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /> Founding Member — $99 lifetime</span>
                <span className="font-mono text-[hsl(var(--muted-foreground))]">{claimed}/{total}</span>
              </div>
              <div className="h-2.5 rounded-full bg-[hsl(var(--secondary))] mt-3 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'hsl(var(--primary))', transition: 'width 0.6s ease' }} />
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2"><b style={{ color: 'hsl(var(--primary))' }}>{seatsLeft} seats left</b> · Pro forever, never pay again</p>
              {deadline && (
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] flex flex-col items-center gap-2">
                  <span className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide">Offer closes in</span>
                  <Countdown deadline={deadline} testId="card-countdown" />
                </div>
              )}
            </div>
          )}
          <Button size="lg" className="rounded-xl mt-8 h-12 px-6" style={{ background: 'hsl(var(--primary))' }} onClick={go} data-testid="cta-start-button">{!offerClosed ? 'Claim your seat' : 'Start free'} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        </div>
      </section>

      <WaitlistBand />

      {/* Footer */}
      <footer className="border-t border-[hsl(var(--border))] bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <Logo />
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3 max-w-xs">The AI teammate for busy, ADHD brains. Your brain isn't broken — it just needed backup.</p>
            <div className="flex gap-3 mt-4">{[Twitter, Linkedin, Github].map((Icon, i) => (<a key={i} href="#" className="h-9 w-9 rounded-xl border border-[hsl(var(--border))] grid place-items-center hover:bg-[hsl(var(--secondary))] transition-colors"><Icon className="h-4 w-4" /></a>))}</div>
          </div>
          {[['Product', ['Today planner', 'Brain dump', 'Focus timer', 'AI agents']], ['Company', ['About', 'Blog', 'Careers']], ['Support', ['Help', 'Accessibility', 'Contact']]].map(([title, links]) => (
            <div key={title}><p className="font-display font-600 mb-3">{title}</p><ul className="space-y-2 text-sm text-[hsl(var(--muted-foreground))]">{links.map((l) => { const href = l === 'Contact' ? 'mailto:info@larkwellapp.com' : l === 'Help' ? '/help' : '#'; return <li key={l}><a href={href} className="hover:text-[hsl(var(--foreground))] transition-colors" data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g,'-')}`}>{l}</a></li>; })}</ul></div>
          ))}
        </div>
        <div className="border-t border-[hsl(var(--border))] py-5"><div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[hsl(var(--muted-foreground))]"><p>© 2026 Larkwell. Made with care for ADHD minds.</p><div className="flex items-center gap-4"><Link to="/privacy" className="hover:text-[hsl(var(--foreground))] transition-colors" data-testid="footer-privacy-link">Privacy &amp; data</Link><Link to="/security" className="hover:text-[hsl(var(--foreground))] transition-colors" data-testid="footer-security-link">Security</Link><Link to="/terms" className="hover:text-[hsl(var(--foreground))] transition-colors" data-testid="footer-terms-link">Terms</Link><p>You're doing better than you think.</p></div></div></div>
      </footer>
    </div>
  );
}
